import { AfterViewInit, Component, effect, OnDestroy, OnInit, signal, Signal } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, PlatformService, ReadonlyRole, UserService } from "@evc/platform";
import { Maybe, WebComponentsConfigService } from '@evc/web-components';
import { TranslateService } from '@ngx-translate/core';
import { AssetBuilderService, LiveAssetManagementService, RoleService } from '@rvi/asset-modules';
import { lastValueFrom, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfigurationItem } from './_objects/tenant-config';
import { User } from './_objects/user';
import { IPC_CHANNEL, STANDALONE } from './_offline/electron.constants';
import { OfflineSetupService } from './_offline/services/offline-setup.service';
import { AppInfoDbService } from './_services/database/app-info-db.service';
import { FeatureFlagService } from "./_services/feature-flag/feature-flag.service";
import { LoginUserOnlineService } from './_services/http-api/login-user-online.service';
import { MeUserService } from './_services/http-api/me-user.service';
import { TenantConfigService } from './_services/http-api/tenant-config.service';
import { LangUpdateService } from './_services/lang-update.service';
import { MarksStateService } from "./_services/mark-state/marks-state.service";
import { NetworkStatusService } from './_services/network-status.service';
import { TitlesStateService } from "./_services/title-state/titles-state.service";
import { UsersStateService } from './_services/user-state/users-state.service';
import { RoutePage } from './app-routes.enum';
import { LANGUAGE, LOCAL_STORAGE, TenantConfig } from './app.constants';
import { ElectronService } from './electron.service';
import { AppInfoSchema } from './_offline/models/app-info.schema';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    providers: [],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {

  public loadedMe = false;
  public isNotAgreedTerms = false;
  public isAnalytics = false;
  public defaultLanguage: string = LANGUAGE.ENGLISH;
  public is403: boolean;
  public isDirectAccess: boolean = false;
  isDefaultOfflineUser = true;

  private overlaySubs: Subscription[] = [];
  private routerSub = new Subscription();
  private onDestroy$ = new Subject();
  public ready = this.plaformService.ready;

  isFullView: boolean = false;

  constructor(
    private appInfoDbService: AppInfoDbService,
    private electronService: ElectronService,
    private langUpdateService: LangUpdateService,
    private loginUserService: LoginUserOnlineService,
    private marksService: MarksStateService,
    private meUserService: MeUserService,
    private networkStatusService: NetworkStatusService,
    private offlineSetupService: OfflineSetupService,
    private router: Router,
    private route: ActivatedRoute,
    private tenantConfigService: TenantConfigService,
    private titlesStateService: TitlesStateService,
    private translate: TranslateService,
    private usersService: UsersStateService,
    private componentConfigService: WebComponentsConfigService,
    private assetBuilderService: AssetBuilderService,
    private liveAssetService: LiveAssetManagementService,
    private featureFlagService: FeatureFlagService,
    private authService: AuthService,
    private assetModuleRoleService: RoleService,
    private userService: UserService,
    private plaformService: PlatformService,
    iconRegistry: MatIconRegistry,
    sanitizer: DomSanitizer,
  ) {
    iconRegistry.addSvgIconSet(
      sanitizer.bypassSecurityTrustResourceUrl('./assets/sprite/sprite.svg')
    );

    iconRegistry.addSvgIconSet(
      sanitizer.bypassSecurityTrustResourceUrl('./assets/images/osc_lib_icons.svg')
    );
    this.componentConfigService.set('ASSETS_BASE_PATH', './assets/');

    this.isDirectAccess = document.URL.includes('?imageId=');

    this.assetBuilderService.pageState$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(state => {
        this.isFullView = state === 'AssetTypePage';
      });

    this.liveAssetService.pageState$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(state => {
        this.isFullView = state === 'LiveAssetPage';
      });

    effect(() => {
      this.setRoleFromPlatform();
    });
  }

  get isDesktop() {
    return this.networkStatusService.isDesktop();
  }

  get isBrianApp() {
    return this.networkStatusService.isBrianMode;
  }

  async ngOnInit() {
    this.lockAppOrientation();
    this.setLanguage();
  }

  ngAfterViewInit() {
    this.initAfterViewInit();
  }

  private setRoleFromPlatform() {
    const role = RoleService.parseRole(this.userService.profile()?.roles[0].name);
    this.assetModuleRoleService.setRole(role);
  }

  private async initAfterViewInit() {
    await this.setApplicationMode();

    if (this.isDesktop) {
      await this.appInfoDbService.putEnvironment().toPromise();
      await this.appInfoDbService.putAppLaunchType().toPromise();
      await this.offlineSetupService.setupDesktopApp(this.route.queryParams, this.onDestroy$);
    }

    this.meUserService.getMe()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: async currentUser => {
          this.isDefaultOfflineUser = currentUser.id === STANDALONE.USER_ID;
          this.networkStatusService.setIsOnPremise(this.isDefaultOfflineUser);
          this.setMe(currentUser).then(() => {
            this.loadedMe = true;
          });
          this.marksService.fetchAndUpdate();
          this.titlesStateService.fetchAndUpdate();
          this.usersService.fetchAndUpdate();
          this.getConfigs();
          this.featureFlagService.fetchAndUpdate();
        },
        error: async error => {
          console.log(error);
          if (!(this.isDesktop && this.offlineSetupService.isUserSelected)) {
            this.is403 = true;
            this.router.navigate([RoutePage.AccessPage]);
          }
        },
        complete: () => {
          console.debug("[AppComponent] ngAfterViewInit() - Retrieving user profile - END");
        }
      });

    this.loginUserSubscribes();
  }
  private async setApplicationMode() {
    const isCancelLogin = document.location.pathname === `/${RoutePage.CancelLogin}` && this.isDesktop;
    const hasInternetConnection = await this.electronService.checkInternetConnectivity();

    if (!this.isDesktop) {
      this.authService.isOffline.set(false);
      await this.authService.init();
      return;
    }

    if (!hasInternetConnection || isCancelLogin) {
      this.authService.isOffline.set(true);
      await this.networkStatusService.switchOfflineMode();
      return;
    }

    let userWasConnected = false;

    try {
      const appInfo: AppInfoSchema = await lastValueFrom(this.appInfoDbService.get());
      userWasConnected = appInfo.currentNetworkMode;
    } catch (error) {
      console.error("[AppComponent] Last connection state retrieval failed:", error);
    }

    try {
      if (userWasConnected) {
        this.authService.isOffline.set(false);
        await this.networkStatusService.switchOnlineMode();
      } else {
        this.authService.isOffline.set(true);
        await this.networkStatusService.switchOfflineMode();
      }

      await this.authService.init();
    } catch (error) {
      console.error("[AppComponent] Authentication service initialization failed:", error);
    }
  }

  private initIsFirstAccess() {
    this.usersService.isFirstAccess$.pipe(takeUntil(this.onDestroy$)).subscribe(isFirst => {
      this.isNotAgreedTerms = isFirst;
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
    this.overlaySubs.forEach(_ => _.unsubscribe());
    this.routerSub.unsubscribe();
  }

  async setMe(me: User) {
    await this.approveUser(me);
    this.isNotAgreedTerms = me && !me.isAgreedTerms;
    this.usersService.setIsFirstAccess(this.isNotAgreedTerms);
    this.initIsFirstAccess();
  }

  public async setLanguage() {
    this.translate.addLangs(LANGUAGE.SUPPORTED_LANGUAGES.map(lang => lang.code));

    let resolvedLanguage = this.defaultLanguage;
    const storedLanguage = localStorage.getItem(LOCAL_STORAGE.LANGUAGE_PREFERANCE);

    if (storedLanguage) {
      this.translate.use(storedLanguage);
      this.langUpdateService.updateLanguage(storedLanguage);
      return;
    }

    if (this.isDesktop) {
      const osLanguage = await this.electronService.getOsLanguage();

      const supportedLanguages = this.translate.getLangs();

      resolvedLanguage =
        supportedLanguages.find(language => language === osLanguage) ??
        supportedLanguages.find(language => language.startsWith(osLanguage.split('-')[0])) ??
        this.defaultLanguage;
    }

    this.langUpdateService.prepare(this.translate, this.onDestroy$);

    this.langUpdateService.updateLanguage(resolvedLanguage);
  }

  public getOsLanguage(): Promise<string> {
    return this.electronService.invoke(IPC_CHANNEL.LANGUAGE.GET_OS_LANGUAGE);
  }

  getConfigs() {
    this.tenantConfigService
      .getTenantConfigs()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        res => {
          this.tenantConfigService.setTenantConfig(res);
          this.setConfigs(res);
        },
        error => console.log(error));

  }

  setConfigs(configs: ConfigurationItem[]) {
    this.isAnalytics = configs.find(_ => _.key === TenantConfig.IsEnabledAnalytics.KEY)?.value as boolean;
  }

  loginUserSubscribes() {
    this.loginUserService
      .onLoginUserWrapper$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe();
  }

  private async approveUser(me: User) {
    if (this.isDesktop) {
      await this.offlineSetupService.approveUserOffline(me);
    } else {
      this.loginUserService.loginUser = me;
      await this.loginUserService.setTenantAndUserProfile();
    }
  }

  private async lockAppOrientation() {
    try {
      if (this.isBrianApp && window.screen.orientation['lock']) {
        console.debug('locking device orientation to landscape');
        await window.screen.orientation['lock']('landscape');
      }
    } catch (error) {
      console.warn('Error locking device orientation:', error.message);
    }
  }

}
